import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Languages, Mic, Volume2, Settings, Loader2 } from "lucide-react";
import TolokyIndicator from "./TolokyIndicator";
import TolokySettingsModal from "./TolokySettingsModal";
import TolokyConsentDialog from "./TolokyConsentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function TolokyControlPanel({ meetingId, meetingTitle, compact = false }) {
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (meetingId) {
      fetchStatus();
      fetchLanguages();
    }
  }, [meetingId]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, settingsRes] = await Promise.all([
        axios.get(`${API}/comms/toloky/meeting/${meetingId}/status`, {
          headers: getHeaders(), withCredentials: true,
        }),
        axios.get(`${API}/comms/toloky/settings`, {
          headers: getHeaders(), withCredentials: true,
        }),
      ]);
      setEnabled(statusRes.data.enabled);
      setSettings(settingsRes.data);
    } catch {}
    setLoading(false);
  };

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/languages`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLanguages(res.data.languages || []);
    } catch {}
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      if (enabled) {
        await axios.post(`${API}/comms/toloky/meeting/${meetingId}/disable`, {}, {
          headers: getHeaders(), withCredentials: true,
        });
        setEnabled(false);
        toast.info("Toloky disabled");
      } else {
        const res = await axios.post(`${API}/comms/toloky/meeting/${meetingId}/enable`, {}, {
          headers: getHeaders(), withCredentials: true,
        });
        setEnabled(true);
        toast.success("Toloky interpreter active");

        // Check consent for voice simulation
        try {
          const statusRes = await axios.get(`${API}/comms/toloky/voice-simulation/status`, {
            headers: getHeaders(), withCredentials: true,
          });
          if (!statusRes.data.allowed && statusRes.data.reason === "Consent required") {
            setShowConsent(true);
          }
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle Toloky");
    }
    setToggling(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading Toloky...
      </div>
    );
  }

  // Compact mode — just a toggle button
  if (compact) {
    return (
      <>
        <Button
          onClick={handleToggle}
          disabled={toggling}
          variant={enabled ? "default" : "outline"}
          size="sm"
          className={`gap-1.5 text-xs rounded-none ${enabled ? "bg-violet-600 hover:bg-violet-700" : ""}`}
        >
          {toggling ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Languages className="w-3 h-3" />
          )}
          {enabled ? "Toloky On" : "Toloky Off"}
        </Button>
        <TolokyConsentDialog
          open={showConsent}
          onOpenChange={setShowConsent}
          onComplete={() => {}}
        />
      </>
    );
  }

  return (
    <div className="bg-white border border-slate-200">
      {/* Header */}
      <TolokyIndicator
        active={enabled}
        sourceLang={settings?.source_language || "auto"}
        targetLang={settings?.target_language || ""}
        mode={settings?.interpretation_mode || "consecutive"}
      />

      {/* Controls */}
      <div className="p-4 space-y-4">
        {/* Main Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? "bg-violet-100" : "bg-slate-100"}`}>
              <Languages className={`w-4 h-4 ${enabled ? "text-violet-600" : "text-slate-400"}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-800">Toloky Interpreter</p>
              <p className="text-[11px] text-slate-500">
                {enabled ? "Active in this meeting" : "Turn on for translation"}
              </p>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={handleToggle} disabled={toggling} />
        </div>

        {enabled && settings && (
          <>
            {/* Language Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-slate-500">I speak</p>
                <Select
                  value={settings.source_language || "auto"}
                  onValueChange={(v) => {
                    const apiValue = v === "auto" ? "" : v;
                    const updated = { ...settings, source_language: apiValue };
                    setSettings(updated);
                    axios.put(`${API}/comms/toloky/settings`, { source_language: apiValue }, {
                      headers: getHeaders(), withCredentials: true,
                    }).catch(() => {});
                  }}
                >
                  <SelectTrigger className="h-8 text-xs rounded-none">
                    <SelectValue placeholder="Auto-detect" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none max-h-60">
                    <SelectItem value="auto">Auto-detect</SelectItem>
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-slate-500">Hear in</p>
                <Select
                  value={settings.target_language}
                  onValueChange={(v) => {
                    const updated = { ...settings, target_language: v };
                    setSettings(updated);
                    axios.put(`${API}/comms/toloky/settings`, { target_language: v }, {
                      headers: getHeaders(), withCredentials: true,
                    }).catch(() => {});
                  }}
                >
                  <SelectTrigger className="h-8 text-xs rounded-none">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none max-h-60">
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Voice Simulation Toggle */}
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border border-slate-200">
              <div className="flex items-center gap-2">
                <Mic className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs text-slate-600">Simulate my voice</span>
              </div>
              <Switch
                checked={settings.voice_simulation_enabled}
                onCheckedChange={(en) => {
                  if (en && !settings.voice_simulation_consent) {
                    setShowConsent(true);
                  } else {
                    const updated = { ...settings, voice_simulation_enabled: en };
                    setSettings(updated);
                    axios.put(`${API}/comms/toloky/settings`, { voice_simulation_enabled: en }, {
                      headers: getHeaders(), withCredentials: true,
                    }).catch(() => {});
                  }
                }}
              />
            </div>

            {/* Mode Select */}
            <div className="flex items-center justify-between text-xs text-slate-600">
              <span>Mode:</span>
              <Select
                value={settings.interpretation_mode}
                onValueChange={(v) => {
                  const updated = { ...settings, interpretation_mode: v };
                  setSettings(updated);
                  axios.put(`${API}/comms/toloky/settings`, { interpretation_mode: v }, {
                    headers: getHeaders(), withCredentials: true,
                  }).catch(() => {});
                }}
              >
                <SelectTrigger className="h-7 w-44 text-xs rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  <SelectItem value="consecutive">Consecutive (turn-based)</SelectItem>
                  <SelectItem value="realtime">Real-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Settings Button */}
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(true)}
            className="text-xs text-slate-500 hover:text-slate-700 gap-1"
          >
            <Settings className="w-3 h-3" />
            Advanced Settings
          </Button>
        </div>
      </div>

      {/* Settings Modal */}
      <TolokySettingsModal open={showSettings} onOpenChange={setShowSettings} />

      {/* Consent Dialog */}
      <TolokyConsentDialog
        open={showConsent}
        onOpenChange={setShowConsent}
        onComplete={(granted) => {
          if (granted && settings) {
            const updated = { ...settings, voice_simulation_enabled: true, voice_simulation_consent: true };
            setSettings(updated);
            axios.put(`${API}/comms/toloky/settings`, {
              voice_simulation_enabled: true, voice_simulation_consent: true,
            }, {
              headers: getHeaders(), withCredentials: true,
            }).catch(() => {});
          }
        }}
      />
    </div>
  );
}
